import { PETS } from '../../content/pets';
import type { SimCtx } from '../context';
import { SIDE_PLAYER, wrapCoord, wrapDelta } from '../world';
import { spawnBullet } from './projectiles';
import { datan2, dcos, dsin } from '../../core/dmath';

export function spawnPets(ctx: SimCtx, petIds: readonly (string | null)[], levels: readonly number[] = []): void {
  const pets = ctx.w.pets;
  for (const player of ctx.w.players) {
    const id = petIds[player.index];
    const defIndex = PETS.findIndex((pet) => pet.id === id);
    if (defIndex < 0) continue;
    const slot = pets.pool.alloc();
    if (!slot) continue;
    pets.owner[slot] = player.index;
    pets.def[slot] = defIndex;
    pets.level[slot] = Math.max(1, Math.min(10, levels[player.index] ?? 1));
    pets.x[slot] = pets.px[slot] = player.x;
    pets.y[slot] = pets.py[slot] = player.y;
    pets.fireT[slot] = PETS[defIndex].fireInterval * 0.5 / (1 + 0.025 * Math.max(0, (levels[player.index] ?? 1) - 1));
    player.pets.push(slot);
  }
}

export function updatePets(ctx: SimCtx, dt: number): void {
  const pets = ctx.w.pets;
  const enemies = ctx.w.enemies;
  const half = ctx.w.half;
  for (let slot = 1; slot < pets.pool.high; slot++) {
    if (!pets.pool.alive[slot]) continue;
    const owner = ctx.w.players[pets.owner[slot]];
    if (!owner?.alive) continue;
    const def = PETS[pets.def[slot]];
    pets.px[slot] = pets.x[slot];
    pets.py[slot] = pets.y[slot];
    const phase = ctx.w.time * 1.8 + pets.owner[slot] * Math.PI * 0.7;
    const tx = wrapCoord(owner.x + dcos(phase) * def.orbitRadius, half);
    const ty = wrapCoord(owner.y + dsin(phase) * def.orbitRadius, half);
    pets.x[slot] = wrapCoord(pets.x[slot] + wrapDelta(tx - pets.x[slot], half) * Math.min(1, dt * 8), half);
    pets.y[slot] = wrapCoord(pets.y[slot] + wrapDelta(ty - pets.y[slot], half) * Math.min(1, dt * 8), half);
    pets.fireT[slot] -= dt;
    if (pets.fireT[slot] > 0) continue;
    let target = 0;
    let best = def.range * def.range;
    for (let i = 1; i < enemies.pool.high; i++) {
      if (!enemies.pool.alive[i] || Math.abs(enemies.z[i]) > 0.5) continue;
      const dx = wrapDelta(enemies.x[i] - pets.x[slot], half);
      const dy = wrapDelta(enemies.y[i] - pets.y[slot], half);
      const d2 = dx * dx + dy * dy;
      if (d2 < best) { best = d2; target = i; }
    }
    if (!target) continue;
    const angle = datan2(wrapDelta(enemies.y[target] - pets.y[slot], half), wrapDelta(enemies.x[target] - pets.x[slot], half));
    pets.facing[slot] = angle;
    const level = Math.max(1, pets.level[slot]);
    const shots = def.ability.shots;
    for (let shot = 0; shot < shots; shot++) {
      const offset = (shot - (shots - 1) * 0.5) * def.ability.spread;
      spawnBullet(ctx.w, ctx.reg, { x: pets.x[slot], y: pets.y[slot], angle: angle + offset, speed: def.bullet.speed, def: def.bullet, side: SIDE_PLAYER, owner: owner.index, damage: def.damage * (1 + 0.08 * (level - 1)) / Math.max(1, shots * 0.72) });
    }
    pets.fireT[slot] += def.fireInterval / (1 + 0.025 * (level - 1));
  }
}
